import { useGetByIdDeviceQuery } from "@/redux/api/deviceApi";
import PropTypes from "prop-types";
import React from "react";
import { IoLocationOutline } from "react-icons/io5";

const LayoutLoadSummary = ({ data, apiData }) => {
  const deviceId = (typeof apiData?.data?.device === "string" ? apiData?.data?.device : apiData?.data?.device?._id) || "";
  const {
    chartTitle,
    isTitleHidden,
    loadSummaryNormalColor = "#10b981",
    loadSummaryNormalMax = 50,
    loadSummaryWarningColor = "#f59e0b",
    loadSummaryWarningMax = 80,
    loadSummaryCriticalColor = "#ef4444",
    loadSummaryBaseline = 0,
    loadSummarySavingsBase,
  } = data || {};

  // rkt query for site info
  const { data: deviceData } = useGetByIdDeviceQuery(
    { id: deviceId, params: { populate: "site", deep: 1 } },
    { skip: !deviceId }
  );

  const value = Number(apiData?.data?.value ?? 0) || 0;
  const unit = apiData?.unit || "kW";
  const peak = Number(apiData?.peakValue ?? 0) || 0;
  const percent = peak > 0 ? (value / peak) * 100 : 0;
  const clampedPercent = Math.min(Math.max(percent, 0), 100);

  // Baseline load input from appearance tab
  const baseline = Number(loadSummaryBaseline || loadSummarySavingsBase || 0);
  const savings = baseline > 0 ? ((baseline - value) / baseline) * 100 : 0;
  const isNegativeSavings = savings < 0;
  const savingsColorClass = isNegativeSavings ? "text-danger" : "text-success";

  // 3 dynamic levels based on direct parameter value
  const normalMax = Number(loadSummaryNormalMax ?? 50);
  const warningMax = Number(loadSummaryWarningMax ?? 80);

  const normalColor = loadSummaryNormalColor || "#10b981";
  const warningColor = loadSummaryWarningColor || "#f59e0b";
  const criticalColor = loadSummaryCriticalColor || "#ef4444";

  let statusText = "NORMAL";
  let statusColor = normalColor;
  let badgeClass = "bg-success-subtle text-success";

  if (value > warningMax) {
    statusText = "CRITICAL";
    statusColor = criticalColor;
    badgeClass = "bg-danger-subtle text-danger";
  } else if (value > normalMax) {
    statusText = "WARNING";
    statusColor = warningColor;
    badgeClass = "bg-warning-subtle text-warning";
  }

  return (
    <div className="w-100 h-100 d-flex flex-column justify-content-between p-2">
      {/* Title & Status */}
      <div className="d-flex justify-content-between align-items-start">
        <div style={{ maxWidth: "70%" }}>
          {!isTitleHidden && (
            <div className="ql fs-5 fw-bold">
              <div className="ql-editor p-0">
                <div dangerouslySetInnerHTML={{ __html: chartTitle || "" }}></div>
              </div>
            </div>
          )}
          <div className="d-flex align-items-center text-secondary small gap-1 text-truncate">
            <IoLocationOutline className="flex-shrink-0" />
            <div className="text-truncate">{deviceData?.data?.site?.name || ""}</div>
          </div>
        </div>
        <span className={`badge rounded-pill ${badgeClass} px-2 py-1 text-uppercase flex-shrink-0`}>
          {statusText}
        </span>
      </div>

      {/* Load & Slider */}
      <div className="my-1">
        <div className="d-flex justify-content-between align-items-baseline mb-1">
          <span className="text-secondary small">Current Load</span>
          <span className="fw-bold fs-5">
            {value.toFixed(2)} {unit}
          </span>
        </div>

        <div className="progress" style={{ height: "7px" }}>
          <div
            className="progress-bar"
            style={{
              width: `${clampedPercent}%`,
              backgroundColor: statusColor,
              transition: "width 0.4s ease, background-color 0.3s ease",
            }}
          />
        </div>

        <div className="d-flex justify-content-between text-secondary small mt-1" style={{ fontSize: "11px" }}>
          <span>{percent.toFixed(2)}% of peak</span>
          <span>
            Peak: {peak.toFixed(2)} {unit}
          </span>
        </div>
      </div>

      {/* Efficiency & Savings */}
      <div className="d-flex justify-content-between align-items-end pt-1 border-top">
        {/* <div>
          <div className="text-secondary small fw-bold">EFFICIENCY</div>
          <div className="fs-5 fw-bold text-danger">0%</div>
        </div> */}
        <div className="ms-auto text-end">
          <div className="text-secondary fw-semibold" style={{ fontSize: "10px", letterSpacing: "0.5px" }}>
            SAVINGS
          </div>
          <div className={`fs-5 fw-bold ${savingsColorClass}`} style={{ lineHeight: 1.1 }}>
            {savings.toFixed(2)}%
          </div>
        </div>
      </div>
    </div>
  );
};

export default LayoutLoadSummary;

LayoutLoadSummary.propTypes = {
  data: PropTypes.object.isRequired,
  mainDashboard: PropTypes.bool,
  apiData: PropTypes.object,
};
